import { CloudFile } from '@/lib/CloudFile'
import { fileToBase64 } from '@/lib/Functions/files'
import { getAppStoragePath, savePhoto } from '@/app/native'
import { App } from '@/app'
import { Filesystem, Directory } from '@capacitor/filesystem'
import { Network } from '@capacitor/network'
import { Capacitor } from '@capacitor/core'
import { LocalFileResourcePath } from './camera/type'
import { PHOTO_DIR } from './constant'
import { normalizeResourcePath } from './util'

// 파일 존재 확인 결과: 'unknown'은 일시 오류로 존재 여부를 단정할 수 없는 상태
export type FileCheckResult = 'exists' | 'missing' | 'unknown'

// 이미지 로드 실패 원인 분류
export type ImageLoadErrorKind = 'local-missing' | 'offline' | 'generic'

class appFileManager {
  private dcimPath = ''
  readonly MAX_IMAGE_DOWNLOAD_RETRY = 3

  /**
   * storage:// 파일의 로컬 사본 존재 여부를 네이티브 stat으로 확인
   * (로컬 HTTP 서버는 없는 파일에도 정상 응답을 줄 수 있어 stat이 신뢰 기준)
   * @param resourcePath storage:// 파일 경로
   * @param customDir 캐시 내 서브디렉토리
   * @returns 로컬 사본 존재 여부
   */
  private async hasLocalCopy(
    resourcePath: `storage://${string}`,
    customDir: string = PHOTO_DIR,
  ): Promise<boolean> {
    try {
      if (this.dcimPath === '') this.dcimPath = await getAppStoragePath()
      const fileName = this.getFileName(resourcePath)
      await Filesystem.stat({ path: `file://${this.dcimPath}/${customDir}/${fileName}` })
      return true
    } catch {
      return false
    }
  }

  /**
   * storage:// 경로 로컬 사본의 바이트 크기(위치는 hasLocalCopy와 같음). 없으면 null
   */
  async localCopySize(resourcePath: string, customDir: string = PHOTO_DIR): Promise<number | null> {
    try {
      if (this.dcimPath === '') this.dcimPath = await getAppStoragePath()
      const fileName = this.getFileName(resourcePath)
      const stat = await Filesystem.stat({
        path: `file://${this.dcimPath}/${customDir}/${fileName}`,
      })
      return stat.size
    } catch {
      return null
    }
  }

  /**
   * 로컬 디렉토리의 파일 이름 목록(readdir 1회) — 여러 파일의 존재 확인을 stat 건별 대신 한 번에.
   * 위치는 hasLocalCopy(Android)·checkFileExistsAndDownloaded의 iOS stat과 같다. 읽지 못하면 null(호출자가 건별 확인으로)
   */
  async listLocalFileNames(customDir: string = PHOTO_DIR): Promise<Set<string> | null> {
    if (!App.isApp()) return null
    try {
      if (Capacitor.getPlatform() === 'ios') {
        const result = await Filesystem.readdir({ path: customDir, directory: Directory.Cache })
        return new Set(result.files.map(file => file.name))
      }
      if (this.dcimPath === '') this.dcimPath = await getAppStoragePath()
      const result = await Filesystem.readdir({ path: `file://${this.dcimPath}/${customDir}` })
      return new Set(result.files.map(file => file.name))
    } catch {
      return null
    }
  }

  /**
   * capacitor 로컬 서버 경로의 파일 존재 여부를 본문 다운로드 없이 확인
   * @param capacitorPath toCapacitorPath로 변환된 로컬 서버 경로
   * @returns 확인된 404만 'missing', 그 외 오류는 'unknown' (없음으로 단정 금지)
   */
  private async checkLocalServerFile(capacitorPath: string): Promise<FileCheckResult> {
    try {
      const res = await fetch(capacitorPath, { headers: { Range: 'bytes=0-0' } })
      res.body?.cancel().catch(() => {}) // 본문 스트림 즉시 해제 (전체 다운로드 방지)
      if (res.ok || res.status === 206 || res.status === 416) {
        // capacitor 로컬 서버는 없는 파일에 404 대신 SPA fallback(index.html)을 줄 수 있음
        // 사진/음성 등 파일 경로에 HTML 응답이 오면 실제로는 파일이 없는 것
        const contentType = res.headers.get('content-type') ?? ''
        if (contentType.includes('text/html')) return 'missing'
        return 'exists' // 416: 빈 파일도 존재
      }
      if (res.status === 404) return 'missing'
      return 'unknown'
    } catch {
      return 'unknown' // 브리지/로컬 서버 오류
    }
  }

  pageEvent: {
    onFabClicked: () => void
  } = {
    onFabClicked: (): void => {},
  }

  /**
   * local-file:// 이나 storage:// 경로를 플랫폼별 Capacitor 경로로 변환
   * @param resourcePath 변환할 ResourcePath
   * @returns Promise<string> 변환된 경로
   */
  async toCapacitorPath(
    resourcePath: LocalFileResourcePath | `storage://${string}`,
    customDir: string = PHOTO_DIR,
  ): Promise<string> {
    if (!App.isApp()) return CloudFile.ResourceUrlToSignedUrl(resourcePath)

    if (this.dcimPath === '') this.dcimPath = await getAppStoragePath()

    const platform = Capacitor.getPlatform()

    if (
      resourcePath.startsWith('local-file://') ||
      resourcePath.startsWith('android-local-file:/') ||
      resourcePath.startsWith('android-file:/')
    ) {
      // 가장 긴 프리픽스부터 제거 (순서 중요)
      const cleanPath = resourcePath
        .replace('android-local-file://', '')
        .replace('android-file://', '')
        .replace('local-file://', '')

      const fileUri = `file://${cleanPath}`
      return Capacitor.convertFileSrc(fileUri)
    }

    if (resourcePath.startsWith('storage://')) {
      const fileName = this.getFileName(resourcePath)
      const localFilePath = `${customDir}/${fileName}`

      if (platform === 'ios') {
        try {
          await Filesystem.stat({
            path: localFilePath,
            directory: Directory.Cache,
          })
        } catch (error) {
          console.warn('[AppFileManager] File not found in iOS Documents:', error)
          return ''
        }
      }

      const fullFilePath = `${this.dcimPath}/${localFilePath}`
      const fileUri = `file://${fullFilePath}`
      return Capacitor.convertFileSrc(fileUri)
    }

    return resourcePath
  }

  getFileName(path: string): string {
    return path.split('/').pop()!
  }

  /**
   * storage:// URL에서 파일을 다운로드하여 캐시 디렉토리에 저장
   * @param resourcePath 다운로드할 storage:// URL
   * @param customDir 캐시 내 서브디렉토리
   * @returns Promise<string> 저장된 파일의 절대 경로
   */
  async downloadResourcePathToDCIM(
    resourcePath: `storage://${string}`,
    customDir: string = PHOTO_DIR,
  ): Promise<string> {
    if (!resourcePath.startsWith('storage://')) {
      throw new Error('다운로드 받을 수 있는 경로가 아닙니다.')
    }

    try {
      const file = await CloudFile.downloadFileFromResourceUrl(resourcePath)
      if (!file) throw new Error('downloadFileFromResourceUrl : File not found')
      return await this.saveResourceBlob(resourcePath, file, customDir)
    } catch (error) {
      console.error('[AppFileManager] downloadResourcePathToDCIM failed:', error)
      throw error
    }
  }

  /**
   * 이미 받은 내용을 storage:// 경로의 로컬 사본으로 저장(downloadResourcePathToDCIM과 같은 위치·이름)
   * @returns 저장된 파일의 절대 경로
   */
  async saveResourceBlob(
    resourcePath: string,
    file: File,
    customDir: string = PHOTO_DIR,
  ): Promise<string> {
    const base64 = await fileToBase64(file)
    if (!base64) throw new Error('Base64 is Invalid')
    return savePhoto({
      fileName: this.getFileName(resourcePath),
      base64,
      dir: customDir,
      permissions: 'private',
    })
  }

  /**
   * storage:// URL 파일이 로컬에 존재하지 않는 경우 다운로드하여 저장
   * @param url storage:// 파일 경로
   * @returns Promise<boolean> 파일이 존재하는 경우 false, 다운로드한 경우 true
   */
  async checkFileExistsAndDownloaded(url: string, customDir: string = PHOTO_DIR): Promise<boolean> {
    if (!App.isApp() || !url || !url.startsWith('storage://')) return false

    try {
      const platform = Capacitor.getPlatform()
      const fileName = this.getFileName(url)

      if (platform === 'ios') {
        // iOS: Filesystem.stat으로 파일 존재 여부 확인
        try {
          await Filesystem.stat({
            path: `${customDir}/${fileName}`,
            directory: Directory.Cache,
          })
          return false // 파일 존재, 다운로드 불필요
        } catch {
          await this.downloadResourcePathToDCIM(url as `storage://${string}`, customDir)
          return true // 파일 다운로드됨
        }
      } else {
        // Android: 네이티브 stat으로 파일 존재 여부 확인
        if (await this.hasLocalCopy(url as `storage://${string}`, customDir)) {
          return false
        }
        await this.downloadResourcePathToDCIM(url as `storage://${string}`, customDir)
        return true
      }
    } catch (downloadError) {
      console.error('[AppFileManager] Download failed for:', url, downloadError)
      throw downloadError
    }
  }

  /**
   * 인터넷 연결 상태에 따라 파일을 최신화하거나 로컬 파일을 사용
   * - 인터넷 연결: 항상 최신 파일 다운로드
   * - 인터넷 미연결: 로컬 파일 확인 후 사용
   * @param url storage:// 파일 경로
   * @param isConnected 인터넷 연결 상태
   * @returns Promise<{downloaded: boolean, localPath: string | null}> 다운로드 여부와 로컬 파일 경로
   */
  async checkAndUpdateFile(
    url: string,
    isConnected: boolean,
  ): Promise<{ downloaded: boolean; localPath: string | null }> {
    if (!App.isApp() || !url || !url.startsWith('storage://')) {
      return { downloaded: false, localPath: null }
    }

    try {
      const platform = Capacitor.getPlatform()
      const fileName = this.getFileName(url)
      const localFilePath = `${PHOTO_DIR}/${fileName}`

      // 인터넷이 연결되어 있으면 항상 최신 파일 다운로드
      if (isConnected) {
        await this.downloadResourcePathToDCIM(url as `storage://${string}`)
        return { downloaded: true, localPath: localFilePath }
      }

      // 인터넷이 연결되어 있지 않으면 로컬 파일 확인

      if (platform === 'ios') {
        try {
          await Filesystem.stat({
            path: localFilePath,
            directory: Directory.Cache,
          })
          return { downloaded: false, localPath: localFilePath }
        } catch {
          return { downloaded: false, localPath: null }
        }
      } else {
        // Android: 네이티브 stat으로 파일 존재 여부 확인
        if (await this.hasLocalCopy(url as `storage://${string}`)) {
          return { downloaded: false, localPath: localFilePath }
        }
        return { downloaded: false, localPath: null }
      }
    } catch (error) {
      console.error('[AppFileManager] checkAndUpdateFile failed for:', url, error)
      throw error
    }
  }

  /**
   * 이미지 로드 실패 시 상황별 원인과 사용자 안내 문구 생성
   * @param src 원본 이미지 URL
   * @returns 실패 원인 분류와 안내 문구
   */
  async getImageLoadError(
    src: string | undefined,
  ): Promise<{ kind: ImageLoadErrorKind; message: string }> {
    const isLocalCapture =
      !!src &&
      (src.startsWith('local-file://') ||
        src.startsWith('android-local-file:/') ||
        src.startsWith('android-file:/'))

    // 촬영 원본(로컬 파일)이 안 열리는 것은 네트워크와 무관 — 파일 자체가 없는 상황
    if (isLocalCapture) {
      return { kind: 'local-missing', message: '사진 파일을 기기에서 찾을 수 없습니다' }
    }

    try {
      if (App.isApp() && !(await Network.getStatus()).connected) {
        return { kind: 'offline', message: '인터넷 연결 후 다시 시도해 주세요' }
      }
    } catch {
      // 네트워크 상태 확인 실패 시 일반 문구로 처리
    }
    return { kind: 'generic', message: '이미지를 불러오지 못했습니다' }
  }

  /**
   * local-file:// URL 파일의 존재 여부를 3단계로 판정
   * @param url local-file:// 파일 경로
   * @returns 확인된 404만 'missing', 일시 오류는 'unknown' (삭제 판단 근거로 쓰면 안 됨)
   */
  async checkFileExistsDetailed(url: string): Promise<FileCheckResult> {
    if (!App.isApp() || !url) return 'missing'

    const platform = Capacitor.getPlatform()

    if (platform === 'ios') {
      // iOS에서는 파일 존재 확인을 skip하고 존재로 간주 (네이티브에서 처리)
      return 'exists'
    }

    try {
      const capacitorPath = await this.toCapacitorPath(url as `local-file://${string}`)
      const result = await this.checkLocalServerFile(capacitorPath)
      if (result !== 'exists') {
        console.warn(`[PhotoGuard] file check '${result}': ${url}`)
      }
      return result
    } catch (error) {
      console.warn(`[PhotoGuard] file check 'unknown' (path convert failed): ${url}`, error)
      return 'unknown' // 경로 변환 실패 등 — 없음으로 단정 금지
    }
  }

  /**
   * local-file:// URL 파일이 존재하는지 확인
   * @param url local-file:// 파일 경로
   * @returns Promise<boolean> 파일이 없음으로 확인된 경우에만 false
   */
  async checkFileExists(url: string): Promise<boolean> {
    return (await this.checkFileExistsDetailed(url)) !== 'missing'
  }

  /**
   * 이미지 URL을 플랫폼에 맞게 변환하는 함수
   * iOS에서는 Capacitor convertFileSrc API를 사용
   * Android에서는 기존 방식
   *
   * @param url - 변환할 이미지 URL
   * @returns Promise<string> - 변환된 URL
   */
  async convertImageUrlForPlatform(url: string): Promise<string> {
    if (!url) return ''

    // 이미 처리된 URL들은 그대로 반환
    if (url.startsWith('data:image/') || url.startsWith('http') || url.startsWith('capacitor://')) {
      return url
    }

    // 웹 환경에서는 CloudFile을 사용
    if (!App.isApp()) {
      if (url.startsWith('storage://')) {
        const { CloudFile } = await import('@/lib/CloudFile')
        return CloudFile.ResourceUrlToSignedUrl(url as `storage://${string}`)
      }
      return url
    }

    // 네이티브 앱 환경
    const platform = Capacitor.getPlatform()
    const convertUrl = normalizeResourcePath(url)

    try {
      if (convertUrl.startsWith('local-file://')) {
        if (platform === 'ios') {
          // iOS에서 local-file:// URL 처리
          const cleanPath = convertUrl.replace('local-file://', '')
          const fileUri = `file://${cleanPath}`
          const convertedUrl = Capacitor.convertFileSrc(fileUri)
          return convertedUrl
        } else {
          // Android에서는 기존 방식
          const { AppFileManager } = await import('@/app/file')
          return await AppFileManager.toCapacitorPath(convertUrl as `local-file://${string}`)
        }
      } else if (convertUrl.startsWith('storage://')) {
        const fileName = convertUrl.split('/').pop() || ''

        if (platform === 'ios') {
          // iOS에서 storage:// URL 처리 - Capacitor convertFileSrc 사용
          try {
            // 파일 존재 여부 확인
            await Filesystem.stat({
              path: `${PHOTO_DIR}/${fileName}`,
              directory: Directory.Cache,
            })

            // 파일이 존재하면 경로 생성
            const { getAppStoragePath } = await import('@/app/native')
            const dcimPath = await getAppStoragePath()
            const fullFilePath = `${dcimPath}/${PHOTO_DIR}/${fileName}`
            const fileUri = `file://${fullFilePath}`

            return Capacitor.convertFileSrc(fileUri)
          } catch {
            // 파일이 없으면 다운로드 시도
            await this.downloadResourcePathToDCIM(convertUrl as `storage://${string}`)

            // 다운로드 후 경로 생성
            const { getAppStoragePath } = await import('@/app/native')
            const dcimPath = await getAppStoragePath()
            const fullFilePath = `${dcimPath}/${PHOTO_DIR}/${fileName}`
            const fileUri = `file://${fullFilePath}`

            return Capacitor.convertFileSrc(fileUri)
          }
        } else {
          // Android에서 storage:// URL 처리
          // 먼저 로컬 사본이 존재하는지 네이티브 stat으로 확인
          if (await this.hasLocalCopy(convertUrl as `storage://${string}`)) {
            return await this.toCapacitorPath(convertUrl as `storage://${string}`)
          }

          // 파일이 없으면 다운로드 시도
          await this.downloadResourcePathToDCIM(convertUrl as `storage://${string}`)

          // 다운로드 후 경로 반환
          return await this.toCapacitorPath(convertUrl as `storage://${string}`)
        }
      }

      return convertUrl
    } catch (error) {
      console.error('Failed to convert image URL:', error)
      return convertUrl // 에러 시 원본 URL 반환
    }
  }
}

export const AppFileManager = new appFileManager()
